import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { Button, Card, Popconfirm, Progress, Switch, Tag, Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';

import type { ShowcaseProject } from '../../api/showcaseProjects';
import {
  ShowcaseImage,
  categoryTag,
  deleteConfirmText,
  formatPeriod,
  statusColors,
  statusTag,
  technologyTags,
  visibleAchievementCount,
} from './showcaseDisplay';
import { BrandColors } from '../../theme/brand';

interface ShowcaseProjectCardProps {
  readonly project: ShowcaseProject;
  readonly canManage: boolean;
  readonly onEdit: (project: ShowcaseProject) => void;
  readonly onDelete: (project: ShowcaseProject) => void;
  readonly onTogglePublished: (project: ShowcaseProject, isPublished: boolean) => void;
}

/**
 * Một dự án ở chế độ xem "Thẻ" — bày đúng những gì thẻ trên website hiển thị, để người quản trị
 * xem trước trang công khai ngay trong ERP.
 *
 * Giống website: nhãn tình trạng và lĩnh vực, tên, khách hàng, mô tả, bốn công nghệ đầu, tiến độ,
 * hai thành tích đầu và ngày. Khác website: thêm thứ tự hiển thị, link demo và các nút quản trị;
 * dự án đang ẩn thì ảnh bị làm nhạt.
 */
export function ShowcaseProjectCard({
  project,
  canManage,
  onEdit,
  onDelete,
  onTogglePublished,
}: ShowcaseProjectCardProps): ReactNode {
  const achievements = project.achievements.slice(0, visibleAchievementCount);
  const moreAchievementCount = project.achievements.length - achievements.length;
  const color = statusColors[project.status];

  return (
    <Card
      size="small"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      styles={{
        body: { flex: 1, display: 'flex', flexDirection: 'column', gap: 10, padding: 16 },
      }}
      cover={
        <div style={{ position: 'relative' }}>
          <ShowcaseImage
            url={project.imageUrl}
            alt={project.title}
            isDimmed={!project.isPublished}
            style={{ borderRadius: 0 }}
          />
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 4 }}>
            {statusTag(project.status)}
            {categoryTag(project.category)}
          </div>
          {!project.isPublished ? (
            <Tag
              icon={<EyeInvisibleOutlined />}
              style={{ position: 'absolute', top: 10, right: 10, marginInlineEnd: 0 }}
            >
              Đang ẩn
            </Tag>
          ) : null}
          <Tooltip title="Thứ tự hiển thị trên website">
            <span
              style={{
                position: 'absolute',
                bottom: 8,
                right: 10,
                padding: '0 8px',
                borderRadius: 10,
                fontSize: 12,
                lineHeight: '20px',
                color: '#fff',
                background: 'rgba(0, 0, 0, 0.55)',
              }}
            >
              #{project.sortOrder.toString()}
            </span>
          </Tooltip>
        </div>
      }
      actions={[
        <Tooltip
          key="published"
          title={project.isPublished ? 'Đang hiện trên website' : 'Đang ẩn khỏi website'}
        >
          <Switch
            size="small"
            checked={project.isPublished}
            disabled={!canManage}
            checkedChildren="Hiện"
            unCheckedChildren="Ẩn"
            onChange={(checked) => { onTogglePublished(project, checked); }}
          />
        </Tooltip>,
        <Button
          key="edit"
          type="text"
          size="small"
          icon={<EditOutlined />}
          disabled={!canManage}
          onClick={() => { onEdit(project); }}
        >
          Sửa
        </Button>,
        <Popconfirm
          key="delete"
          title={deleteConfirmText.title}
          description={deleteConfirmText.description}
          okText="Xóa"
          cancelText="Không"
          okButtonProps={{ danger: true }}
          disabled={!canManage}
          onConfirm={() => { onDelete(project); }}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={!canManage}>
            Xóa
          </Button>
        </Popconfirm>,
      ]}
    >
      <div>
        <Typography.Paragraph
          strong
          style={{ fontSize: 15, marginBottom: 2 }}
          ellipsis={{ rows: 2, tooltip: project.title }}
        >
          {project.title}
        </Typography.Paragraph>
        <Typography.Text type="secondary" style={{ fontSize: 13 }} ellipsis={{ tooltip: project.clientName }}>
          <BankOutlined /> {project.clientName}
        </Typography.Text>
      </div>

      <Typography.Paragraph
        type="secondary"
        style={{ fontSize: 13, marginBottom: 0 }}
        ellipsis={{ rows: 2, tooltip: project.description }}
      >
        {project.description}
      </Typography.Paragraph>

      {technologyTags(project.technologies)}

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.5 }}>
            TIẾN ĐỘ
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 12, color }}>
            {project.progressPercent.toString()}%
          </Typography.Text>
        </div>
        <Progress
          percent={project.progressPercent}
          showInfo={false}
          size="small"
          strokeColor={color}
          style={{ margin: 0 }}
        />
      </div>

      {achievements.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {achievements.map((achievement) => (
            <Typography.Text key={achievement} style={{ fontSize: 13 }} ellipsis={{ tooltip: achievement }}>
              <CheckCircleFilled style={{ color: BrandColors.green, marginInlineEnd: 6 }} />
              {achievement}
            </Typography.Text>
          ))}
          {moreAchievementCount > 0 ? (
            <Tooltip title={project.achievements.slice(visibleAchievementCount).join(' · ')}>
              <Typography.Text type="secondary" style={{ fontSize: 12, cursor: 'default' }}>
                +{moreAchievementCount.toString()} thành tích khác
              </Typography.Text>
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 10,
          borderTop: `1px solid ${BrandColors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          <CalendarOutlined /> {formatPeriod(project)}
        </Typography.Text>
        {project.demoUrl === null ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Chưa có demo
          </Typography.Text>
        ) : (
          <a href={project.demoShareUrl ?? project.demoUrl} target="_blank" rel="noopener noreferrer">
            Xem demo <ExportOutlined />
          </a>
        )}
      </div>
    </Card>
  );
}
